import { Vector3, type Camera, type Group } from "three";

import { PHYSICAL_CARD_HEIGHT, PHYSICAL_CARD_WIDTH } from "./PhysicalCard3D";

export interface ScreenRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export type HandScreenLayout = Readonly<Record<string, ScreenRect>>;

const CARD_CORNERS = [
  new Vector3(-PHYSICAL_CARD_WIDTH / 2, -PHYSICAL_CARD_HEIGHT / 2, 0),
  new Vector3(PHYSICAL_CARD_WIDTH / 2, -PHYSICAL_CARD_HEIGHT / 2, 0),
  new Vector3(PHYSICAL_CARD_WIDTH / 2, PHYSICAL_CARD_HEIGHT / 2, 0),
  new Vector3(-PHYSICAL_CARD_WIDTH / 2, PHYSICAL_CARD_HEIGHT / 2, 0),
] as const;
const PROJECTED_CORNER = new Vector3();

export function projectCardScreenRect(
  group: Group,
  camera: Camera,
  viewportWidth: number,
  viewportHeight: number,
): ScreenRect {
  group.updateWorldMatrix(true, false);
  camera.updateMatrixWorld();
  let minimumX = Number.POSITIVE_INFINITY;
  let minimumY = Number.POSITIVE_INFINITY;
  let maximumX = Number.NEGATIVE_INFINITY;
  let maximumY = Number.NEGATIVE_INFINITY;

  for (const corner of CARD_CORNERS) {
    const projected = PROJECTED_CORNER.copy(corner).applyMatrix4(group.matrixWorld).project(camera);
    const x = (projected.x * 0.5 + 0.5) * viewportWidth;
    const y = (-projected.y * 0.5 + 0.5) * viewportHeight;
    minimumX = Math.min(minimumX, x);
    minimumY = Math.min(minimumY, y);
    maximumX = Math.max(maximumX, x);
    maximumY = Math.max(maximumY, y);
  }

  return {
    left: minimumX,
    top: minimumY,
    width: Math.max(0, maximumX - minimumX),
    height: Math.max(0, maximumY - minimumY),
  };
}

export function screenRectsDiffer(
  current: HandScreenLayout,
  next: HandScreenLayout,
  tolerance = 1.5,
): boolean {
  const currentKeys = Object.keys(current);
  const nextKeys = Object.keys(next);
  if (currentKeys.length !== nextKeys.length) return true;
  return nextKeys.some((key) => {
    const first = current[key];
    const second = next[key];
    return (
      first === undefined ||
      second === undefined ||
      Math.abs(first.left - second.left) > tolerance ||
      Math.abs(first.top - second.top) > tolerance ||
      Math.abs(first.width - second.width) > tolerance ||
      Math.abs(first.height - second.height) > tolerance
    );
  });
}

export function roundedScreenRect(rect: ScreenRect): ScreenRect {
  return {
    left: Math.round(rect.left),
    top: Math.round(rect.top),
    width: Math.round(rect.width),
    height: Math.round(rect.height),
  };
}
